import { createTheme } from '@mui/material/styles';
import { fontWeight } from '@mui/system';

export const supportTheme = {
  color: {
    accent: "#B1ECB0",
    accentHover: "#A0E49F",
    accentMuted: "rgba(177, 236, 176, 0.15)",
    accentMutedStrong: "rgba(177, 236, 176, 0.10)",
    accentBorder: "rgba(177, 236, 176, 0.20)",
    accentBorderStrong: "rgba(177, 236, 176, 0.30)",
    accentRing: "rgba(177, 236, 176, 0.40)",
    overlay: "rgba(0, 0, 0, 0.50)",
    panel: "#111827",
    field: "#2B2B2B",
    fieldBorder: "rgba(75, 85, 99, 0.80)",
    context: "rgba(31, 41, 55, 0.30)",
    contextBorder: "rgba(55, 65, 81, 0.50)",
    attachmentRow: "rgba(31, 41, 55, 0.50)",
    text: "#FFFFFF",
    textTitle: "#F3F4F6",
    textPrimary: "#E5E7EB",
    textSecondary: "#D1D5DB",
    textMuted: "#9CA3AF",
    textSubtle: "#6B7280",
    textFaint: "#4B5563",
    textOnAccent: "#111827",
    error: "#F87171",
    errorMuted: "rgba(239, 68, 68, 0.10)",
    errorBorder: "rgba(239, 68, 68, 0.20)",
  },

  size: {
    panelMaxWidth: 640,
    panelMaxHeight: 768,
    panelOffsetTop: 68,
    panelInset: '16px',
    overlayZ: 80,
    headerIcon: 32,
    successIcon: 48,
    fieldHeight: 40,
    descriptionMinHeight: 176,
    buttonHeight: 36,
    iconSm: 16,
    iconXs: 14,
  },

  space: {
    headerX: 20,
    headerY: 16,
    bodyX: 20,
    bodyY: 16,
    footerX: 20,
    footerY: 14,
    fieldGap: 14,
    typePriorityGap: 12,
    labelGap: 6,
  },

  radius: {
    panel: "8px",
    control: "6px",
    pill: 9999,
  },

  type: {
    title: { fontSize: 16, fontWeight: 800, color: "#F3F4F6" },
    subtitle: { fontSize: 12, fontWeight: 400, color: "#7d7e7f", lineHeight: 1.2 },
    label: { fontSize: 13, fontWeight: 600, color: "#9CA3AF", pb: 0.5 },
    body: { fontSize: 14, fontWeight: 400, color: "#FFFFFF" },
    helper: { fontSize: 12, fontWeight: 400, color: "#79797b" },
    contextLabel: { fontSize: 12, fontWeight: 400, color: "#5d5d5e", textTransform: "uppercase" },
    contextValue: { fontSize: 12, fontWeight: 400, color: "#D1D5DB" },
    button: { fontSize: 12, fontWeight: 600 },
    ticketKey: { fontSize: 18, fontWeight: 600, color: "#B1ECB0", family: "ui-monospace, monospace" },
    hyperlink: { fontSize: 14, fontWeight: 800, color: "#B1ECB0", family: "ui-monospace, monospace" },
  },

  component: {
    overlay: {
      background: "rgba(0, 0, 0, 0.50)",
    },
    panel: {
      background: "rgb(23, 23, 23)",
      border: "1px solid rgba(52, 65, 52)",
      radius: 8,
      maxWidth: "640px",
      maxHeight: "768px",
      shadow: "0 25px 50px -12px rgba(0, 0, 0, 0.50)",
      position: 'absolute',
      top: '30px',
      right: 0
    },
    header: {
      padding: "16px 20px",
      borderBottom: "1px solid rgba(68, 71, 74, 0.5)",
    },
    headerIcon: {
      size: 32,
      background: "rgba(177, 236, 176, 0.15)",
      border: "1px solid rgba(177, 236, 176, 0.30)",
      color: "#B1ECB0",
    },
    context: {
      background: "rgba(26, 26, 26,)",
      border: "1px solid rgba(47, 47, 47)",
      borderRadius: '8px',
      padding: "10px 12px",
    },
    field: {
      height: 40,
      background: 'rgb(38, 38, 38)',
      border: "1px solid rgb(75, 76, 79)",
      borderRadius: '8px',
      color: "#FFFFFF",
      placeholder: "#4e525a",
      focusBorder: "#B1ECB0",
      focusRing: "0 0 0 1px rgba(177, 236, 176, 0.40)",
      padding: 9      
    },
    description: {
      minHeight: 176,
      paddingY: 10,
    },
    attachmentDrop: {
      height: 40,
      border: "1px dashed rgba(75, 85, 99, 0.80)",
      hoverBorder: "rgba(177, 236, 176, 0.40)",
      color: "#9CA3AF",
    },
    attachmentRow: {
      background: "rgba(31, 41, 55, 0.50)",
      border: "1px solid rgba(55, 65, 81, 0.40)",
      radius: 6,
      padding: "6px 10px",
    },
    error: {
      background: "rgba(239, 68, 68, 0.10)",
      border: "1px solid rgba(239, 68, 68, 0.20)",
      color: "#F87171",
      radius: 6,
      padding: "10px 12px",
    },
    footer: {
      padding: "14px 20px",
      borderTop: "1px solid rgba(55, 65, 81, 0.50)",
      display: "flex",
      justifyContent: "flex-end",
      gap: 12,
    },
    primaryButton: {
      height: 36,
      background: "#B1ECB0",
      color: "#111827",
      borderRadius: "6px",
      paddingX: 2,
      '&:hover': {
        backgroundColor: "#d8ffd8",
      },
        '&:disabled': {
        backgroundColor: "#2f3b2f",
      },
    },
    secondaryButton: {
      height: 36,
      // border: "1px solid rgba(75, 85, 99, 0.80)",
      color: "#E5E7EB",
      hoverBackground: "rgba(31, 41, 55, 0.60)",
      borderRadius: "6px",
      paddingX: 2,
      '&:hover': {
        backgroundColor: "#444444",
      },
    },
    cancelButton: {
      height: 36,
      color: "#9CA3AF",
      hoverColor: "#E5E7EB",
      paddingX: 12,
    },
    success: {
      iconSize: 48,
      iconBackground: "rgba(177, 236, 176, 0.15)",
      iconBorder: "1px solid rgba(177, 236, 176, 0.30)",
      ticketBackground: "rgba(177, 236, 176, 0.10)",
      ticketBorder: "1px solid rgba(177, 236, 176, 0.25)",
    },
  },
};



const customTheme = createTheme({
  palette: {
    primary: {
      main: '#000000', // Customize the primary color
    },
    secondary: {
      main: '#2196f3', // Customize the secondary color
    },
    // You can customize other colors like error, warning, info, etc.
  },
  // You can also customize typography, spacing, and other theme properties here
});

export default customTheme;